import { apiAxios, supportAxios, SUPPORT_BASE_URL } from 'utils/axios';

// Repoint target (Phase 2). When REACT_APP_SUPPORT_URL is set, the widget talks
// to the standalone `ipscore-support` service: routes are mounted at ROOT
// (`/conversations`, not `/support/conversations`) and identity travels as the
// `access_token` cookie (sent cross-origin with credentials), NOT a Bearer
// header. When it is unset, behaviour is unchanged — the legacy integrated api
// path (`/support/*` via apiAxios + Bearer). The legacy path is removed at
// cutover once the standalone service is deployed and verified.
const STANDALONE = SUPPORT_BASE_URL.length > 0;

// Legacy base mirrors utils/axios.ts for the EventSource (which cannot go
// through the axios instance) when talking to the integrated api.
const LEGACY_BASE = (
  process.env.REACT_APP_BASE_URL || 'https://api.ipscore.app'
).replace(/\/+$/, '');

// Base + path prefix the widget uses for REST + SSE.
export const SUPPORT_API_BASE = STANDALONE ? SUPPORT_BASE_URL : LEGACY_BASE;
const PATH_PREFIX = STANDALONE ? '' : '/support';

// True when talking to the standalone service: the SSE EventSource must be
// opened with credentials so the access_token cookie is sent cross-origin.
export const SUPPORT_STANDALONE = STANDALONE;

export interface CreateConversationResponse {
  conversation_id: number;
  visitor_token: string;
  status: string;
}

const authHeaders = (token?: string): Record<string, string> =>
  token ? { Authorization: `Bearer ${token}` } : {};

/**
 * Start a conversation. Identity is derived server-side — from the access_token
 * cookie (standalone) or the Bearer token (legacy). We never send a
 * client-asserted user_id.
 */
export const createConversation = async (params: {
  message: string;
  locale?: string;
  visitorToken?: string;
  token?: string;
}): Promise<CreateConversationResponse> => {
  const { message, locale, visitorToken, token } = params;
  const body = { message, locale, visitor_token: visitorToken };
  if (STANDALONE) {
    const { data } = await supportAxios.post('/conversations', body);
    return data;
  }
  const { data } = await apiAxios.post('/support/conversations', body, {
    headers: authHeaders(token),
  });
  return data;
};

export interface HistoryMessage {
  id: number;
  sender: string;
  body: string;
  attachment?: {
    id: string;
    kind: 'image' | 'file';
    name?: string;
    mime?: string;
    size?: number;
  };
}

/**
 * Fetch the full stored history (visitor + operator + system) to rehydrate the
 * thread after a reload — the visitor's own past messages live only server-side.
 */
export const fetchHistory = async (params: {
  conversationId: number;
  visitorToken?: string;
  token?: string;
}): Promise<HistoryMessage[]> => {
  const { conversationId, visitorToken, token } = params;
  const qs = new URLSearchParams();
  if (visitorToken) qs.set('visitor_token', visitorToken);
  const path = STANDALONE
    ? `/conversations/${conversationId}/messages`
    : `/support/conversations/${conversationId}/messages`;
  const client = STANDALONE ? supportAxios : apiAxios;
  const { data } = await client.get(`${path}?${qs.toString()}`, {
    headers: token ? authHeaders(token) : {},
  });
  return data?.messages ?? [];
};

/** Send a follow-up visitor message into an existing conversation. */
export const postMessage = async (params: {
  conversationId: number;
  message: string;
  visitorToken?: string;
  token?: string;
}): Promise<void> => {
  const { conversationId, message, visitorToken, token } = params;
  if (STANDALONE) {
    const headers: Record<string, string> = {};
    if (visitorToken) headers['X-Visitor-Token'] = visitorToken;
    await supportAxios.post(
      `/conversations/${conversationId}/messages`,
      { message },
      { headers }
    );
    return;
  }
  const headers: Record<string, string> = { ...authHeaders(token) };
  if (visitorToken) headers['X-Visitor-Token'] = visitorToken;
  await apiAxios.post(
    `/support/conversations/${conversationId}/messages`,
    { message },
    { headers }
  );
};

export interface UploadAttachmentResponse {
  message_id: number;
  attachment_id: string;
  kind: string;
}

/**
 * Upload an image/file into a conversation (multipart/form-data). The server
 * enforces the size cap + MIME whitelist and stores the file under a uuid name;
 * we only forward the file, an optional caption, and the visitor token. axios
 * v1 auto-sets the multipart Content-Type (with boundary) for FormData bodies.
 */
export const uploadAttachment = async (params: {
  conversationId: number;
  file: File;
  caption?: string;
  visitorToken?: string;
  token?: string;
}): Promise<UploadAttachmentResponse> => {
  const { conversationId, file, caption, visitorToken, token } = params;
  const form = new FormData();
  form.append('file', file);
  if (caption) form.append('caption', caption);

  if (STANDALONE) {
    const headers: Record<string, string> = {};
    if (visitorToken) headers['X-Visitor-Token'] = visitorToken;
    const { data } = await supportAxios.post(
      `/conversations/${conversationId}/attachments`,
      form,
      { headers }
    );
    return data;
  }
  const headers: Record<string, string> = { ...authHeaders(token) };
  if (visitorToken) headers['X-Visitor-Token'] = visitorToken;
  const { data } = await apiAxios.post(
    `/support/conversations/${conversationId}/attachments`,
    form,
    { headers }
  );
  return data;
};

/**
 * Build the URL that serves a stored attachment. EventSource-style query auth:
 * the visitor_token (and, for a logged-in visitor, the token) travel as query
 * params so an <img> / download link can fetch it cross-origin.
 */
export const attachmentUrl = (
  attachmentId: string,
  visitorToken?: string,
  token?: string
): string => {
  const qs = new URLSearchParams();
  if (token) qs.set('token', token);
  if (visitorToken) qs.set('visitor_token', visitorToken);
  const query = qs.toString();
  return `${SUPPORT_API_BASE}${PATH_PREFIX}/attachments/${attachmentId}${
    query ? `?${query}` : ''
  }`;
};

/**
 * Build the SSE stream URL. EventSource cannot set an Authorization header, so
 * the token / visitor_token travel as query params. (Cross-origin credentialed
 * EventSource also carries the access_token cookie when the service enables
 * credentialed CORS.)
 */
export const buildStreamUrl = (params: {
  conversationId: number;
  visitorToken?: string;
  token?: string;
  lastEventId?: number;
}): string => {
  const { conversationId, visitorToken, token, lastEventId } = params;
  const qs = new URLSearchParams();
  if (token) qs.set('token', token);
  if (visitorToken) qs.set('visitor_token', visitorToken);
  if (lastEventId) qs.set('last_event_id', String(lastEventId));
  const query = qs.toString();
  return `${SUPPORT_API_BASE}${PATH_PREFIX}/conversations/${conversationId}/stream${
    query ? `?${query}` : ''
  }`;
};
